import React from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { StreamingRightsFragment } from '~/graphqlTypes/StreamingRightsFragment';
import { SimpleCountryFragment } from '~/graphqlTypes/SimpleCountryFragment';
import type { Controls } from '~/hooks/useAsyncModal/types';
import { AudioFileAvailabilityFragment } from '~/graphqlTypes/AudioFileAvailabilityFragment';
import { CountryInclusionMode } from '~/graphqlTypes/globalTypes';
import { CountriesDropdown } from '~/components/CountriesDropdown/CountriesDropdown';
import { CountryAvailabilityFragment_countriesAndDates } from '~/graphqlTypes/CountryAvailabilityFragment';

export interface AudioFileAvailabilityModalProps {
  /** All known streaming rights. */
  allStreamingRights: StreamingRightsFragment[];
  /** Controls provided by `useAsyncMocal`. */
  modalControls: Controls<AudioFileAvailabilityFragment>;
  /** If true, the "right" dropdown is read only. */
  readonlyRight?: boolean;
  /** Modal title. */
  title?: string;
}

export const AudioFileAvailabilityModal: React.FC<AudioFileAvailabilityModalProps> = ( props ) => {
  const {
    allStreamingRights,
    modalControls,
    readonlyRight = false,
    title = modalControls.isNew ? 'Create Audio File Availability' : 'Edit Audio File Availability',
  } = props;
  const { localModel, save, close, canSave, isNew, updateField, isSaving, serverError } = modalControls;

  const updateRight = ( streamingRight: StreamingRightsFragment ) => updateField( 'streamingRights', streamingRight );

  const updateMode = ( mode: CountryInclusionMode ) =>
    updateField(
      'countryAvailability',
      localModel?.countryAvailability ? { ...localModel.countryAvailability, mode } : { mode },
    );

  const updateCountries = ( countries: SimpleCountryFragment[] ) => {
    const countriesAndDates = countries.map<CountryAvailabilityFragment_countriesAndDates>( ( country ) => ( {
      __typename: 'CountryDate',
      country,
    } ) );

    updateField(
      'countryAvailability',
      localModel?.countryAvailability
        ? { ...localModel.countryAvailability, countriesAndDates }
        : { countriesAndDates },
    );
  };

  return (
    <Modal show={ true } onHide={ close }>
      <Modal.Header closeButton>
        <Modal.Title>{ title }</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="streaming-right">
            <Form.Label>Streaming Right</Form.Label>
            <Select
              isDisabled={ readonlyRight }
              value={ localModel?.streamingRights }
              options={ allStreamingRights }
              formatOptionLabel={ ( { name, description } ) => (
                <>
                  { name } &mdash; { description }
                </>
              ) }
              getOptionValue={ ( right ) => right.name }
              placeholder="Select a streaming right..."
              onChange={ ( right ) => right && updateRight( right ) }
            />
          </Form.Group>
          <Form.Group controlId="country-inclusion">
            <Form.Label>Countries</Form.Label>
            <Form.Control
              as="select"
              value={ localModel?.countryAvailability?.mode }
              onChange={ ( event ) => updateMode( event.target.value as CountryInclusionMode ) }
              size="sm"
            >
              <option value={ CountryInclusionMode.include_all }>All</option>
              <option value={ CountryInclusionMode.include }>Only include...</option>
              <option value={ CountryInclusionMode.exclude }>Include all except...</option>
            </Form.Control>
          </Form.Group>
          { localModel?.countryAvailability?.mode !== CountryInclusionMode.include_all && (
            <Form.Group controlId="streaming-right">
              <Form.Label>Inclusion list</Form.Label>
              <CountriesDropdown
                isMulti
                onChange={ ( countries ) => updateCountries( [...countries] ) }
                value={ localModel?.countryAvailability?.countriesAndDates?.map( ( { country } ) => country ) || [] }
              />
            </Form.Group>
          ) }
        </Form>
      </Modal.Body>
      <Modal.Footer>
        { serverError && <p className="text-danger">{ serverError }</p> }
        { isSaving && <Spinner animation="border" /> }
        <Button variant="primary" onClick={ close }>
          Cancel
        </Button>
        <Button variant="primary" onClick={ save } disabled={ !canSave }>
          { isNew ? 'Create' : 'Save changes' }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
